// Stock table → "Stock History" → View: one product's full movement history
// (create, adjustment, sale, return, combo, damage/scrap/RTV) from the
// stockMovement table. Separate from the Stock Ledger, which hides sales.
import * as React from "react"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useSearchParams } from "react-router-dom"
import { ColumnDef } from "@tanstack/react-table"
import { ArrowLeft } from "lucide-react"
import moment from "moment"
import { DateRange } from "react-day-picker"
import ApparelMenu from "./SubComponents/apparelMenu"
import { getStockMovementHistoryAction } from "../Store/actions/apparelAction"
import { DataTable } from "../shared/data-table"
import { StatusBadge } from "../shared/status-badge"
import { DateRangePicker } from "./Reports/DateRangePicker"
import { Button } from "ui/button"
import type { BadgeProps } from "ui/badge"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "ui/select"

const PAGE_SIZE = 20;

type Variant = NonNullable<BadgeProps["variant"]>;

// action → [label, badge variant]. Unknown actions fall back to the raw key.
const ACTIONS: Record<string, [string, Variant]> = {
    PURCHASE: ["Stock Created", "success"],
    ADMIN_STOCK_ADD: ["Adjustment +", "accent"],
    ADMIN_STOCK_REMOVE: ["Adjustment −", "accent"],
    SALE_CONSUMPTION: ["Sold", "destructive"],
    SALE_REVERSAL: ["Sale Reversed", "secondary"],
    SALES_RETURN: ["Sales Return", "warning"],
    COMBO_BUILD: ["Combo Build", "success"],
    COMBO_ALLOCATION: ["Used in Combo", "warning"],
    COMBO_RELEASE: ["Combo Release", "secondary"],
    COMBO_RETURN_BREAKUP: ["Combo Return", "secondary"],
    COMBO_RETURN_BREAKUP_REVERSAL: ["Combo Return Undone", "warning"],
    SOLD_TO_INSTOCK: ["Sold → In Stock", "secondary"],
    SOLD_TO_DAMAGED: ["Sold → Damaged", "destructive"],
    ADD_TO_DAMAGED: ["Damaged", "destructive"],
    REMOVE_FROM_DAMAGED: ["Damaged Removed", "secondary"],
    MARK_AS_RETURN_TO_VENDOR: ["Return to Vendor", "warning"],
    ADD_TO_SCRAP: ["Scrap", "destructive"],
    REMOVE_FROM_SCRAP: ["Scrap Removed", "secondary"],
};

const columns: ColumnDef<any, any>[] = [
    {
        id: "createdOn",
        header: "Date & Time",
        cell: ({ row }) => <span className="whitespace-nowrap">{moment(row.original?.createdOn).format("DD-MM-YY, hh:mm A")}</span>,
    },
    {
        id: "action",
        header: "Action",
        cell: ({ row }) => {
            const [label, variant] = ACTIONS[row.original?.action] ?? [row.original?.action, undefined];
            return <StatusBadge status={label} variant={variant} />;
        },
    },
    {
        id: "quantity",
        header: () => <div className="text-right">Qty</div>,
        cell: ({ row }) => {
            const qty = Number(row.original?.quantity);
            return (
                <div className={`text-right tabular-nums ${qty > 0 ? "text-emerald-600" : qty < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                    {qty > 0 ? `+${qty}` : qty}
                </div>
            );
        },
    },
    {
        id: "balanceAfter",
        header: () => <div className="text-right">Balance</div>,
        cell: ({ row }) => <div className="text-right tabular-nums">{row.original?.balanceAfter ?? "—"}</div>,
    },
    {
        id: "reference",
        header: "Reference",
        cell: ({ row }) => <span className="block max-w-[320px] truncate text-muted-foreground" title={row.original?.reference}>{row.original?.reference || "—"}</span>,
    },
    {
        id: "createdByName",
        header: "By",
        cell: ({ row }) => <span className="whitespace-nowrap">{row.original?.createdByName || "—"}</span>,
    },
];

const StockMovementHistory = () => {
    const [searchParams] = useSearchParams();
    const productDetailId = searchParams.get("productDetailId") || "";
    const productId = searchParams.get("productId") || "";
    const [page, setPage] = useState(1);
    const [action, setAction] = useState("");
    const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { getStockMovementHistoryData, getStockMovementHistoryLoad } = useSelector((state: any) => state.ApparelReducerState);

    const historyResult = getStockMovementHistoryData?.getStockMovementHistory;

    useEffect(() => {
        if (!productDetailId) return;
        dispatch(getStockMovementHistoryAction({
            productDetailId,
            action: action || undefined,
            fromDate: dateRange?.from ? moment(dateRange.from).format("YYYY-MM-DD") : undefined,
            toDate: dateRange?.to ? moment(dateRange.to).format("YYYY-MM-DD") + " 23:59:59" : undefined,
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
        }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productDetailId, page, action, dateRange])

    return (
        <div className="mz h-full overflow-y-auto px-6 py-4">
            <ApparelMenu selectedKey="1" />
            <DataTable
                columns={columns}
                data={historyResult?.rows || []}
                loading={getStockMovementHistoryLoad}
                title={`Stock History${productId ? ` — ${productId}` : ""}`}
                headerActions={
                    <Button variant="outline" size="sm" onClick={() => navigate("/inventoryManagement")}>
                        <ArrowLeft className="mr-1 h-4 w-4" /> Back to Stock
                    </Button>
                }
                toolbar={
                    <>
                        <DateRangePicker value={dateRange} onChange={(r: DateRange | undefined) => { setDateRange(r); setPage(1); }} disableFuture />
                        <Select value={action || "ALL"} onValueChange={(v) => { setAction(v === "ALL" ? "" : v); setPage(1); }}>
                            <SelectTrigger className="w-[200px]"><SelectValue placeholder="All actions" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="ALL">All actions</SelectItem>
                                {Object.entries(ACTIONS).map(([value, [label]]) => (
                                    <SelectItem key={value} value={value}>{label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </>
                }
                pagination={{
                    page: historyResult?.page?.currentPage || page,
                    total: historyResult?.page?.totalCount || 0,
                    pageSize: PAGE_SIZE,
                    onChange: setPage,
                }}
            />
        </div>
    )
}

export default StockMovementHistory
